import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AddToTopicModal from '../AddToTopicModal';
import { apiErrorMessage } from '../../services/errorMessage';
import { withReturnTo } from '../common/returnPath';
import type { TopicKeyword, TrendStory } from '../../types';

/**
 * Writing a page about a term the model declined to propose one for.
 *
 * THE OVERRIDE. Most spikes are a name or a score with no durable question
 * under them, and the model saying so is usually right — but it is a judgement,
 * not a verdict, and somebody who knows their market may see the angle it
 * missed. Making its opinion a gate rather than an opinion would be the wrong
 * trade, and the cost of being wrong here is one page nobody has to publish.
 *
 * Deliberately quiet: a text link, not a button. It should be findable by
 * someone who has decided, and invisible to someone skimming for what the page
 * recommends.
 *
 * The term has no Keyword row until somebody decides it is worth tracking, so
 * this resolves one first — the same bridge the spiking list has always used.
 */
export default function WritePageAnyway({ story }: { story: TrendStory }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState<{ id: number; value: string } | null>(null);
  const [resolving, setResolving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A late-arriving brief means research finished while this was on screen;
  // there is no page to write "anyway" if it now has proposals.
  const briefId = story.brief?.id ?? null;

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(t);
  }, [error]);

  if (briefId == null) return null;

  const start = async () => {
    // Already tracked? The card carries the id, so skip the round trip.
    if (story.keyword_id) {
      setKeyword({ id: story.keyword_id, value: story.term });
      return;
    }
    setResolving(true);
    setError(null);
    try {
      const kw = await api.trackSpikingTerm(story.term);
      setKeyword({ id: kw.keyword_id, value: kw.value });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setResolving(false);
    }
  };

  const handleFiled = async (created?: TopicKeyword[]) => {
    const topicKeywordId = created?.[0]?.id;
    if (!topicKeywordId) {
      setError('That keyword did not land anywhere - try picking a topic again.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.createPageFromTrend(briefId, {
        topic_keyword_id: topicKeywordId,
      });
      navigate(
        withReturnTo(
          `/page-builder/${result.managed_page_id}`,
          `${location.pathname}${location.search}`,
          'Trends',
        ),
      );
    } catch (e) {
      setError(apiErrorMessage(e));
      setBusy(false);
      setKeyword(null);
    }
  };

  return (
    <>
      <button
        onClick={start}
        disabled={resolving || busy}
        className="text-xs text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-600 disabled:opacity-50 dark:text-gray-500 dark:hover:text-gray-300"
        title={`Write a page about "${story.term}" anyway`}
      >
        {resolving || busy ? 'Working…' : 'Write one anyway'}
      </button>

      {keyword && (
        <AddToTopicModal
          keywords={[keyword]}
          purpose={{
            title: 'Where should this page live?',
            blurb: `A page about "${story.term}". Nothing was proposed for this one, so it starts from the stories and demand behind the spike.`,
            verb: 'Create page in',
          }}
          onClose={() => setKeyword(null)}
          onSuccess={handleFiled}
        />
      )}

      {error && (
        <span className="text-xs text-red-600 dark:text-red-400">{error}</span>
      )}
    </>
  );
}
